import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { Card, Badge, Button } from 'components/ui';
import {
  VideoCameraIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  XMarkIcon,
  PlusIcon,
  LinkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { toast } from 'sonner';
import { getMeetings, cancelMeeting } from 'services/googleMeetService';
import MeetModal from './GoogleMeetTab/MeetModal';
import MeetActions from './GoogleMeetTab/MeetActions';
import { ConfirmModal } from 'components/shared/ConfirmModal';
import { getLocalDateTime, formatDisplayDate, formatTime } from "utils/dateUtils";

const GoogleMeetTab = ({ leadData, isMobile = false }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMeetModal, setShowMeetModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelConfirmState, setCancelConfirmState] = useState('pending');
  const [cancelConfirmLoading, setCancelConfirmLoading] = useState(false);
  const [meetingToCancel, setMeetingToCancel] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedMeetingForPopover, setSelectedMeetingForPopover] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const fetchMeetings = async () => {
    if (!leadData?.id) return;

    setLoading(true);
    try {
      // Get current month (1-12) and year (4-digit) from the displayed calendar
      const month = currentDate.getMonth() + 1; // JavaScript months are 0-11, so add 1
      const year = currentDate.getFullYear();

      const result = await getMeetings({
        leadId: leadData.id,
        pageNumber: 1,
        pageSize: 10,
        month: month,
        year: year
      });

      if (result.success) {
        setMeetings(result.data || []);
      } else {
        console.error('Error fetching meetings:', result.error);
        toast.error(result.error || 'Failed to load Google Meet meetings');
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error('Failed to load Google Meet meetings');
    } finally {
      setLoading(false);
    }
  };
  // Fetch meetings from API
  useEffect(() => {
    fetchMeetings();
  }, [leadData?.id, currentDate.getMonth(), currentDate.getFullYear()]);

  const handleMeetingSaved = (savedMeeting) => {


    fetchMeetings();
    setShowMeetModal(false);
    setSelectedMeeting(null);
    setSelectedDate(null);
  };

  const handleEditMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowMeetModal(true);
  };

  const handleCreateMeeting = (date = null) => {
    setSelectedMeeting(null);
    setSelectedDate(date);
    setShowMeetModal(true);
  };

  const handleDateClick = (date) => {
    // Set the time to 9:00 AM for the selected date
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(9, 0, 0, 0);
    handleCreateMeeting(selectedDateTime);
  };

  const handleJoinMeeting = (meetLink) => {
    window.open(meetLink, '_blank');
  };

  const handleCancelMeeting = (meeting) => {
    setMeetingToCancel(meeting);
    setShowCancelConfirm(true);
    setCancelConfirmState('pending');
  };

  const confirmCancelMeeting = async () => {
    if (!meetingToCancel) return;

    setCancelConfirmLoading(true);

    try {
      const result = await cancelMeeting({
        meetingId: meetingToCancel.id,
        eventId: meetingToCancel.googleEventId
      });

      if (result.success) {
        // Remove meeting from list
        // setMeetings(prev => prev.filter(m => m.id !== meetingToCancel.id));
        setCancelConfirmState('success');
        fetchMeetings()
        toast.success('Meeting cancelled successfully');

        // Close modal after a short delay to show success state
        setTimeout(() => {
          setShowCancelConfirm(false);
          setMeetingToCancel(null);
          setCancelConfirmState('pending');
        }, 1500);
      } else {
        setCancelConfirmState('error');
        toast.error(result.error || 'Failed to cancel meeting');
      }
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      setCancelConfirmState('error');
      toast.error('Failed to cancel meeting');
    } finally {
      setCancelConfirmLoading(false);
    }
  };

  const closeCancelConfirm = () => {
    setShowCancelConfirm(false);
    setMeetingToCancel(null);
    setCancelConfirmState('pending');
    setCancelConfirmLoading(false);
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };



  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dateStr = formatDate(current);
      const currentDateObj = new Date(current);

      // Get all meetings that occur on this day (including multi-day meetings)
      const dayMeetings = meetings.filter(m => {
        const meetingStart = getLocalDateTime(m.startTime);
        const meetingEnd = getLocalDateTime(m.endTime);

        // Normalize dates to compare just the date part
        const meetingStartDate = new Date(meetingStart.getFullYear(), meetingStart.getMonth(), meetingStart.getDate());
        const meetingEndDate = new Date(meetingEnd.getFullYear(), meetingEnd.getMonth(), meetingEnd.getDate());
        const currentDate = new Date(currentDateObj.getFullYear(), currentDateObj.getMonth(), currentDateObj.getDate());

        // Check if current date falls within meeting start and end dates
        return currentDate >= meetingStartDate && currentDate <= meetingEndDate;
      });

      days.push({
        date: new Date(current),
        dateStr,
        isCurrentMonth: current.getMonth() === month,
        isToday: formatDate(current) === formatDate(new Date()),
        meetings: dayMeetings
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentDate, meetings]);

  // Navigation functions
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: 'info', icon: <ClockIcon className="w-3 h-3" />, text: 'Scheduled' },
      completed: { color: 'success', icon: <CheckCircleIcon className="w-3 h-3" />, text: 'Completed' },
      cancelled: { color: 'error', icon: <XMarkIcon className="w-3 h-3" />, text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.scheduled;

    return (
      <Badge
        color={config.color}
        className="flex items-center gap-1 w-fit"
      >
        {config.icon}
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-100 flex items-center gap-2">
              <VideoCameraIcon className="w-5 h-5 text-primary-600" />
              Google Meet Interviews
            </h3>
            <p className="text-sm text-gray-500 dark:text-dark-300 mt-1">
              Schedule and manage video interviews with {leadData?.name || 'the candidate'}
            </p>
          </div>
          <Button
            color="primary"
            onClick={handleCreateMeeting}
            disabled={leadData?.IsStudent}
            className={`flex cursor-pointer items-center gap-2 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <PlusIcon className="w-4 h-4" />
            Schedule Meeting
          </Button>
        </div>

      </Card>

      {/* Calendar View */}
      <Card className="p-6">
        {/* Calendar Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <Button
            variant="flat"
            onClick={goToPrevMonth}
            className="flex items-center gap-2 sm:w-auto w-full justify-center"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </Button>

          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-dark-100 text-center">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>

          <Button
            variant="flat"
            onClick={goToNextMonth}
            className="flex items-center gap-2 sm:w-auto w-full justify-center"
          >
            <span className="hidden sm:inline">Next</span>
            <span className="sm:hidden">Next</span>
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>

        {/* Calendar Grid with Horizontal Scroll */}
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          <div className="min-w-[600px] grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-600 dark:text-gray-400 py-2 min-w-[80px]">
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {calendarDays.map((day, index) => (
              <div
                key={index}
                className={`
                    h-[120px] p-2 border-r border-b border-gray-100 dark:border-gray-700 last:border-r-0 relative
                    transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 overflow-hidden min-w-[80px] cursor-pointer
                    ${day.isCurrentMonth
                    ? 'bg-white dark:bg-gray-900'
                    : 'bg-gray-50/50 dark:bg-gray-800/30'
                  }
                    ${day.isToday
                    ? 'bg-blue-50 dark:bg-blue-900/10 ring-2 ring-blue-500 ring-inset'
                    : ''
                  }
                  `}
                onClick={() => !leadData?.IsStudent && handleDateClick(day.date)}
              >
                {/* Date Number */}
                <div className={`
                    flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium mb-2
                    ${day.isCurrentMonth
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-400 dark:text-gray-600'
                  }
                    ${day.isToday
                    ? 'bg-blue-600 text-white shadow-lg'
                    : ''
                  }
                  `}>
                  {day.date.getDate()}
                </div>

                {/* Meetings for this day */}
                <div className="flex-1 overflow-y-auto space-y-1 max-h-[70px]">
                  {day.meetings.map((meeting, mIndex) => {
                    const meetingStart = new Date(meeting.startTime);
                    const meetingEnd = new Date(meeting.endTime);
                    const meetingStartDate = new Date(meetingStart.getFullYear(), meetingStart.getMonth(), meetingStart.getDate());
                    const meetingEndDate = new Date(meetingEnd.getFullYear(), meetingEnd.getMonth(), meetingEnd.getDate());
                    const currentDate = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate());

                    // Check if this is a multi-day meeting
                    const isMultiDay = meetingStartDate.getTime() !== meetingEndDate.getTime();
                    const isFirstDay = currentDate.getTime() === meetingStartDate.getTime();
                    const isLastDay = currentDate.getTime() === meetingEndDate.getTime();

                    // Determine styling based on meeting status
                    const isCancelled = meeting.status === 'cancelled';
                    const statusStyles = isCancelled
                      ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
                      : 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800';

                    return (
                      <Popover key={mIndex} className="relative">
                        <PopoverButton
                          as="div"
                          className={`text-xs px-2 py-1 rounded-md cursor-pointer transition-all duration-200 hover:shadow-sm ${statusStyles} ${isMultiDay ? (isFirstDay ? 'rounded-r-none' : isLastDay ? 'rounded-l-none' : 'rounded-none') : ''
                            } ${isCancelled ? 'opacity-60 line-through' : ''}`}
                          title={`${isCancelled ? 'Cancelled: ' : ''}${isMultiDay ? 'Multi-day: ' : ''}${formatTime(meeting.startTime)} - ${meeting.title}`}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering date click
                            console.log('Meeting data:', meeting);
                            setSelectedMeetingForPopover(meeting);
                          }}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1 min-w-0">
                              {isFirstDay && (
                                isCancelled ?
                                  <XMarkIcon className="w-3 h-3 flex-shrink-0" /> :
                                  <ClockIcon className="w-3 h-3 flex-shrink-0" />
                              )}
                              <span className="truncate font-medium">
                                {isFirstDay ? formatTime(meeting.startTime) : '→'}
                              </span>
                            </div>
                            <div className={`w-2 h-2 ${isCancelled ? 'bg-red-500' : isMultiDay ? 'bg-orange-500' : 'bg-blue-500'} rounded-full flex-shrink-0`}></div>
                          </div>
                          {isFirstDay && meeting.title && (
                            <div className="mt-1 text-xs opacity-75 truncate">
                              {meeting.title}
                            </div>
                          )}
                        </PopoverButton>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out"
                          enterFrom="opacity-0 translate-y-2"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-2"
                        >
                          <PopoverPanel
                            anchor={{ to: "bottom start", gap: 8 }}
                            className="z-50 w-80 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 shadow-lg dark:shadow-none"
                          >
                            {({ close }) => (
                              <div className="p-4" onClick={(e) => e.stopPropagation()}>
                                {/* Header with Close Button */}
                                <div className="flex items-start justify-between mb-3">
                                  <div className="min-w-0 flex-1">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-100 truncate">
                                      {meeting.title}
                                    </h3>
                                    {meeting.LeadName && (
                                      <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                                        Lead: {meeting.LeadName}
                                      </p>
                                    )}
                                    {!meeting.LeadName && meeting.leadName && (
                                      <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                                        Lead: {meeting.leadName}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); close(); }}
                                    className="cursor-pointer text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0 ml-2"
                                  >
                                    <XMarkIcon className="w-5 h-5" />
                                  </button>
                                </div>

                                {/* Description */}
                                {meeting.description && (
                                  <p className="text-xs text-gray-600 dark:text-dark-300 mb-3">
                                    {meeting.description}
                                  </p>
                                )}

                                {/* Status Badge */}
                                <div className="mb-3 ">
                                  {getStatusBadge(meeting.status)}
                                </div>

                                {/* Start and End Date/Time */}
                                <div className="space-y-2 mb-3 text-xs text-gray-600 dark:text-dark-300">
                                  <div className="flex items-center gap-2">
                                    <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="font-medium">Start:</span>
                                    <span>{formatDisplayDate(meeting.startTime)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="font-medium">End:</span>
                                    <span>{formatDisplayDate(meeting.endTime)}</span>
                                  </div>
                                </div>

                                {/* Meeting Link */}
                                {meeting.meetLink && meeting.status !== 'cancelled' && (
                                  <div className="mb-3 bg-gray-50 dark:bg-dark-700 rounded-md p-2">
                                    <div
                                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 rounded p-1 transition-colors group"
                                      onClick={() => {
                                        navigator.clipboard.writeText(meeting.meetLink);
                                        toast.success('Meeting link copied to clipboard!');
                                      }}
                                    >
                                      <LinkIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                      <p className="text-xs text-gray-500 dark:text-dark-400 truncate flex-1">
                                        {meeting.meetLink}
                                      </p>
                                      <span className="text-xs text-primary-600 dark:text-primary-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                        Copy
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* Actions */}
                                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                                  <MeetActions
                                    meeting={meeting}
                                    onJoinMeeting={handleJoinMeeting}
                                    onEditMeeting={handleEditMeeting}
                                    onCancelMeeting={handleCancelMeeting}
                                    disabled={leadData?.IsStudent}
                                  />
                                </div>
                              </div>
                            )}
                          </PopoverPanel>
                        </Transition>
                      </Popover>
                    );
                  })}
                </div>

                {/* Day indicator dot for meetings */}
                {day.meetings.length > 0 && (
                  <div className="absolute top-1 right-1">
                    <div className={`w-2 h-2 rounded-full ${day.meetings.some(m => m.status === 'cancelled')
                      ? 'bg-red-500'
                      : 'bg-blue-500'
                      }`}></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Create/Edit Meeting Modal */}
      <MeetModal
        isOpen={showMeetModal}
        onClose={() => {
          setShowMeetModal(false);
          setSelectedMeeting(null);
          setSelectedDate(null);
        }}
        leadData={leadData}
        meeting={selectedMeeting}
        selectedDate={selectedDate}
        onMeetingSaved={handleMeetingSaved}
      />

      {/* Cancel Meeting Confirmation Modal */}
      <ConfirmModal
        show={showCancelConfirm}
        onClose={closeCancelConfirm}
        onOk={confirmCancelMeeting}
        confirmLoading={cancelConfirmLoading}
        state={cancelConfirmState}
        buttonColor="error"
        messages={{
          pending: {
            title: "Cancel Meeting",
            description: `Are you sure you want to cancel the meeting ${meetingToCancel?.title}? This action cannot be undone and the Google Meet link will be deactivated.`,
            actionText: "Cancel Meeting"
          },
          success: {
            title: "Meeting Cancelled",
            description: "The meeting has been successfully cancelled and the Google Meet link has been deactivated.",
            actionText: "Done"
          },
          error: {
            title: "Cancellation Failed",
            description: "Failed to cancel the meeting. Please check your internet connection and try again.",
            actionText: "Retry"
          }
        }}
      />
    </div>
  );
};

export default GoogleMeetTab;
